/**
 * Hardware Utility for Proctoring SDK
 * Monitors media devices (camera, microphone, screen) for errors and disconnections.
 * Provides dialog-based recovery mechanisms and unified error handling.
 */

class Hardware {
  constructor() {
    this.camera_stream = null;
    this.screen_stream = null;
    this.health_check_interval = null;
    this.is_monitoring = false;
    this.shadow_root = null;
    this.recovery_dialog = null;

    this.is_recovering = {
      camera: false,
      screen: false,
    };

    // Track which devices have been reported as disconnected (prevent duplicates)
    this.disconnected_reported = {
      camera: false,
      mic: false,
      screen: false,
    };

    // Device state tracking
    this.device_state = {
      camera: { active: false, muted: false },
      mic: { active: false, muted: false },
      screen: { active: false, muted: false },
    };

    // Last known devices
    this.known_devices = {
      video: [],
      audio: [],
    };

    // Configuration
    this.config = {
      recovery_enabled: true,
      recovery_delay_ms: 1000,
      health_check_interval_ms: 5000,
      show_recovery_dialog: true, // Show dialog instead of auto-recovery
    };

    // Callbacks
    this.callbacks = {
      on_device_disconnected: null,
      on_device_reconnected: null,
      on_recovery_started: null,
      on_device_change: null,
      on_device_muted: null,
      on_device_unmuted: null,
      on_error: null,
    };

    // Dialog styles
    this.dialog_styles = `
            .hardware-recovery-overlay {
                position: fixed;
                inset: 0;
                background: rgba(0, 0, 0, 0.7);
                display: flex;
                align-items: center;
                justify-content: center;
                z-index: 999999;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            }

            .hardware-recovery-dialog {
                background: #1e293b;
                border-radius: 16px;
                padding: 32px;
                max-width: 420px;
                width: 90%;
                text-align: center;
                box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
                animation: dialog-appear 0.2s ease-out;
            }

            @keyframes dialog-appear {
                from { opacity: 0; transform: scale(0.95); }
                to { opacity: 1; transform: scale(1); }
            }

            .hardware-recovery-icon {
                width: 64px;
                height: 64px;
                background: rgba(239, 68, 68, 0.15);
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                margin: 0 auto 20px;
            }

            .hardware-recovery-icon svg {
                width: 32px;
                height: 32px;
                color: #ef4444;
            }

            .hardware-recovery-icon.warning {
                background: rgba(245, 158, 11, 0.15);
            }

            .hardware-recovery-icon.warning svg {
                color: #f59e0b;
            }

            .hardware-recovery-title {
                font-size: 20px;
                font-weight: 600;
                color: #f1f5f9;
                margin-bottom: 8px;
            }

            .hardware-recovery-message {
                font-size: 14px;
                color: #94a3b8;
                line-height: 1.6;
                margin-bottom: 24px;
            }

            .hardware-recovery-buttons {
                display: flex;
                gap: 12px;
                justify-content: center;
            }

            .hardware-recovery-btn {
                padding: 12px 24px;
                font-size: 14px;
                font-weight: 500;
                border-radius: 8px;
                cursor: pointer;
                transition: all 0.2s;
                border: none;
                display: flex;
                align-items: center;
                gap: 8px;
            }

            .hardware-recovery-btn:disabled {
                opacity: 0.6;
                cursor: not-allowed;
            }

            .hardware-recovery-btn-primary {
                background: #3b82f6;
                color: white;
            }

            .hardware-recovery-btn-primary:hover:not(:disabled) {
                background: #2563eb;
            }

            .hardware-recovery-btn-secondary {
                background: #334155;
                color: #f1f5f9;
            }

            .hardware-recovery-btn-secondary:hover:not(:disabled) {
                background: #475569;
            }

            .hardware-recovery-btn svg {
                width: 18px;
                height: 18px;
            }

            @keyframes spin {
                to { transform: rotate(360deg); }
            }

            .spin {
                animation: spin 1s linear infinite;
            }
        `;

    // Icons
    this.icons = {
      camera: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/></svg>`,
      mic: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/></svg>`,
      screen: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" x2="16" y1="21" y2="21"/><line x1="12" x2="12" y1="17" y2="21"/></svg>`,
      alert: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" x2="12" y1="8" y2="12"/><line x1="12" x2="12.01" y1="16" y2="16"/></svg>`,
      loader: `<svg class="spin" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>`,
      refresh: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M8 16H3v5"/></svg>`,
    };
  }

  /**
   * Initialize the hardware monitor
   * @param {Object} options - Configuration and callbacks
   */
  init(options = {}) {
    // Merge config
    if (options.config) {
      this.config = { ...this.config, ...options.config };
    }

    // Set shadow root for dialogs (optional, will create own if not provided)
    if (options.shadow_root) {
      this.shadow_root = options.shadow_root;
    }

    // Merge callbacks (support both snake_case and camelCase)
    if (options.callbacks) {
      this.callbacks.on_device_disconnected =
        options.callbacks.on_device_disconnected ||
        options.callbacks.onDeviceDisconnected ||
        null;
      this.callbacks.on_device_reconnected =
        options.callbacks.on_device_reconnected ||
        options.callbacks.onDeviceReconnected ||
        null;
      this.callbacks.on_recovery_started =
        options.callbacks.on_recovery_started ||
        options.callbacks.onRecoveryStarted ||
        null;
      this.callbacks.on_recovery_failed =
        options.callbacks.on_recovery_failed ||
        options.callbacks.onRecoveryFailed ||
        null;
      this.callbacks.on_device_change =
        options.callbacks.on_device_change ||
        options.callbacks.onDeviceChange ||
        null;
      this.callbacks.on_device_muted =
        options.callbacks.on_device_muted ||
        options.callbacks.onDeviceMuted ||
        null;
      this.callbacks.on_device_unmuted =
        options.callbacks.on_device_unmuted ||
        options.callbacks.onDeviceUnmuted ||
        null;
      this.callbacks.on_error =
        options.callbacks.on_error || options.callbacks.onError || null;
    }

    // Setup device change listener
    this.setupDeviceChangeListener();

    // Create dialog container if showing recovery dialogs
    if (this.config.show_recovery_dialog) {
      this.createDialogContainer();
    }

    console.log("[Hardware] Initialized");
  }

  /**
   * Create dialog container with shadow DOM
   */
  createDialogContainer() {
    // Remove existing container
    const existing = document.getElementById("hardware-recovery-container");
    if (existing) {
      existing.remove();
    }

    // Create new container with shadow DOM
    const container = document.createElement("div");
    container.id = "hardware-recovery-container";
    container.style.cssText =
      "position: fixed; top: 0; left: 0; width: 0; height: 0; z-index: 999999;";

    this.shadow_root = container.attachShadow({ mode: "open" });

    // Inject styles
    const style = document.createElement("style");
    style.textContent = this.dialog_styles;
    this.shadow_root.appendChild(style);

    document.body.appendChild(container);
  }

  /**
   * Start monitoring media streams
   * @param {MediaStream} camera_stream - Camera/mic stream
   * @param {MediaStream} screen_stream - Screen share stream
   */
  monitorStreams(camera_stream, screen_stream) {
    this.camera_stream = camera_stream;
    this.screen_stream = screen_stream;

    // Monitor camera stream tracks
    if (camera_stream) {
      const video_tracks = camera_stream.getVideoTracks();
      const audio_tracks = camera_stream.getAudioTracks();

      video_tracks.forEach((track) => this.monitorTrack(track, "camera"));
      audio_tracks.forEach((track) => this.monitorTrack(track, "mic"));

      this.device_state.camera.active = video_tracks.some(
        (t) => t.readyState === "live",
      );
      this.device_state.mic.active = audio_tracks.some(
        (t) => t.readyState === "live",
      );
    }

    // Monitor screen stream tracks
    if (screen_stream) {
      const screen_tracks = screen_stream.getVideoTracks();
      screen_tracks.forEach((track) => this.monitorTrack(track, "screen"));

      this.device_state.screen.active = screen_tracks.some(
        (t) => t.readyState === "live",
      );
    }

    // Start health check loop
    this.startHealthCheck();

    // Enumerate initial devices
    this.enumerateDevices();

    this.is_monitoring = true;
    console.log("[Hardware] Started monitoring streams");
  }

  /**
   * Monitor a single track for events
   * @param {MediaStreamTrack} track - The track to monitor
   * @param {string} device_type - 'camera', 'mic', or 'screen'
   */
  monitorTrack(track, device_type) {
    // Track ended event
    track.onended = () => {
      console.log(`[Hardware] ${device_type} track ended`);
      this.handleTrackEnded(device_type);
    };

    // Track muted event
    track.onmute = () => {
      console.log(`[Hardware] ${device_type} track muted`);
      this.device_state[device_type].muted = true;
      this.callbacks.on_device_muted?.(device_type);
    };

    // Track unmuted event
    track.onunmute = () => {
      console.log(`[Hardware] ${device_type} track unmuted`);
      this.device_state[device_type].muted = false;
      this.callbacks.on_device_unmuted?.(device_type);
    };
  }

  /**
   * Handle track ended event
   * @param {string} device_type - 'camera', 'mic', or 'screen'
   */
  handleTrackEnded(device_type) {
    // Prevent duplicate handling - only report once per device
    if (this.disconnected_reported[device_type]) {
      return;
    }

    this.device_state[device_type].active = false;
    this.disconnected_reported[device_type] = true;

    const error = new Error(`${device_type} track ended unexpectedly`);
    this.callbacks.on_device_disconnected?.(device_type, error);

    // Show recovery dialog if enabled, otherwise attempt auto-recovery
    // Only show dialog for camera (which includes mic) or screen - not both camera AND mic
    if (this.config.recovery_enabled) {
      const recovery_type = device_type === "screen" ? "screen" : "camera";

      // If camera or mic disconnected, mark both as reported to prevent duplicate dialogs
      if (device_type === "camera" || device_type === "mic") {
        this.disconnected_reported.camera = true;
        this.disconnected_reported.mic = true;
      }

      if (this.config.show_recovery_dialog) {
        this.showRecoveryDialog(device_type);
      } else {
        this.attemptRecovery(device_type);
      }
    }
  }

  /**
   * Show recovery dialog for disconnected device
   * @param {string} device_type - 'camera', 'mic', or 'screen'
   */
  showRecoveryDialog(device_type) {
    // Don't show multiple dialogs
    if (this.recovery_dialog) {
      return;
    }

    const recovery_type = device_type === "screen" ? "screen" : "camera";

    const titles = {
      camera: "Camera Disconnected",
      mic: "Microphone Disconnected",
      screen: "Screen Share Stopped",
    };

    const messages = {
      camera:
        "Your camera has been disconnected or access was revoked. Please click the button below to reconnect your camera and microphone.",
      mic: "Your microphone has been disconnected or access was revoked. Please click the button below to reconnect your camera and microphone.",
      screen:
        "Your screen share has stopped. This may happen if you closed the shared window or stopped sharing. Please click below to share your screen again.",
    };

    const button_labels = {
      camera: "Reconnect Camera",
      mic: "Reconnect Microphone",
      screen: "Share Screen Again",
    };

    const overlay = document.createElement("div");
    overlay.className = "hardware-recovery-overlay";
    overlay.innerHTML = `
            <div class="hardware-recovery-dialog">
                <div class="hardware-recovery-icon warning">
                    ${this.icons[device_type] || this.icons.alert}
                </div>
                <h2 class="hardware-recovery-title">${titles[device_type]}</h2>
                <p class="hardware-recovery-message">${messages[device_type]}</p>
                <div class="hardware-recovery-buttons">
                    <button class="hardware-recovery-btn hardware-recovery-btn-primary" id="hw-recover-btn">
                        ${this.icons.refresh}
                        <span>${button_labels[device_type]}</span>
                    </button>
                </div>
            </div>
        `;

    this.shadow_root.appendChild(overlay);
    this.recovery_dialog = overlay;

    // Handle recover button click
    const recoverBtn = overlay.querySelector("#hw-recover-btn");
    recoverBtn.onclick = async () => {
      recoverBtn.disabled = true;
      recoverBtn.innerHTML = `${this.icons.loader}<span>Requesting access...</span>`;

      try {
        const stream = await this.performRecovery(recovery_type);
        if (stream) {
          this.hideRecoveryDialog();
          this.callbacks.on_device_reconnected?.(device_type, stream);
        }
      } catch (error) {
        // Recovery failed - show error and allow retry
        recoverBtn.disabled = false;
        recoverBtn.innerHTML = `${this.icons.refresh}<span>Try Again</span>`;

        const message = overlay.querySelector(".hardware-recovery-message");
        message.textContent = `Failed to reconnect: ${error.message}. Please try again.`;
        message.style.color = "#ef4444";
      }
    };
  }

  /**
   * Hide the recovery dialog
   */
  hideRecoveryDialog() {
    if (this.recovery_dialog) {
      this.recovery_dialog.remove();
      this.recovery_dialog = null;
    }
  }

  /**
   * Perform the actual recovery (called from dialog button click)
   * @param {string} recovery_type - 'camera' or 'screen'
   * @returns {Promise<MediaStream>}
   */
  async performRecovery(recovery_type) {
    this.callbacks.on_recovery_started?.(recovery_type);

    if (recovery_type === "camera") {
      return await this.recoverCamera();
    } else {
      return await this.recoverScreen();
    }
  }

  /**
   * Setup device change listener
   */
  setupDeviceChangeListener() {
    if (!navigator.mediaDevices) return;

    navigator.mediaDevices.ondevicechange = async () => {
      console.log("[Hardware] Device change detected");

      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const video_devices = devices.filter((d) => d.kind === "videoinput");
        const audio_devices = devices.filter((d) => d.kind === "audioinput");

        // Notify callback
        this.callbacks.on_device_change?.({
          video: video_devices,
          audio: audio_devices,
        });

        // Check if current device was removed
        this.checkDeviceAvailability(video_devices, audio_devices);

        // Update known devices
        this.known_devices.video = video_devices;
        this.known_devices.audio = audio_devices;
      } catch (error) {
        console.error("[Hardware] Failed to enumerate devices:", error);
        this.callbacks.on_error?.(error);
      }
    };
  }

  /**
   * Check if currently used devices are still available
   * @param {MediaDeviceInfo[]} video_devices - Available video devices
   * @param {MediaDeviceInfo[]} audio_devices - Available audio devices
   */
  checkDeviceAvailability(video_devices, audio_devices) {
    // Skip if already reported or recovery dialog is open
    if (this.recovery_dialog) {
      return;
    }

    if (this.camera_stream && !this.disconnected_reported.camera) {
      const video_track = this.camera_stream.getVideoTracks()[0];
      const audio_track = this.camera_stream.getAudioTracks()[0];

      // Check video device
      if (video_track && video_track.readyState === "ended") {
        const settings = video_track.getSettings();
        const device_id = settings.deviceId;
        const still_available = video_devices.some(
          (d) => d.deviceId === device_id,
        );

        if (!still_available) {
          console.log("[Hardware] Camera device no longer available");
          this.handleTrackEnded("camera");
        }
      }

      // Check audio device (only if camera not already reported)
      if (
        audio_track &&
        audio_track.readyState === "ended" &&
        !this.disconnected_reported.mic
      ) {
        const settings = audio_track.getSettings();
        const device_id = settings.deviceId;
        const still_available = audio_devices.some(
          (d) => d.deviceId === device_id,
        );

        if (!still_available) {
          console.log("[Hardware] Microphone device no longer available");
          this.handleTrackEnded("mic");
        }
      }
    }
  }

  /**
   * Enumerate available devices
   */
  async enumerateDevices() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      this.known_devices.video = devices.filter((d) => d.kind === "videoinput");
      this.known_devices.audio = devices.filter((d) => d.kind === "audioinput");
      return {
        video: this.known_devices.video,
        audio: this.known_devices.audio,
      };
    } catch (error) {
      console.error("[Hardware] Failed to enumerate devices:", error);
      return { video: [], audio: [] };
    }
  }

  /**
   * Start periodic health check
   */
  startHealthCheck() {
    if (this.health_check_interval) {
      clearInterval(this.health_check_interval);
    }

    this.health_check_interval = setInterval(() => {
      this.checkStreamHealth();
    }, this.config.health_check_interval_ms);
  }

  /**
   * Check health of all monitored streams
   */
  checkStreamHealth() {
    // Skip health check if recovery dialog is open
    if (this.recovery_dialog) {
      return;
    }

    // Check camera stream
    if (this.camera_stream && !this.disconnected_reported.camera) {
      const video_tracks = this.camera_stream.getVideoTracks();
      const audio_tracks = this.camera_stream.getAudioTracks();

      const video_active = video_tracks.some((t) => t.readyState === "live");
      const audio_active = audio_tracks.some((t) => t.readyState === "live");

      // Detect state change - only if previously active
      if (this.device_state.camera.active && !video_active) {
        console.log("[Hardware] Camera became inactive during health check");
        this.handleTrackEnded("camera");
      } else if (
        this.device_state.mic.active &&
        !audio_active &&
        !this.disconnected_reported.mic
      ) {
        console.log(
          "[Hardware] Microphone became inactive during health check",
        );
        this.handleTrackEnded("mic");
      }

      this.device_state.camera.active = video_active;
      this.device_state.mic.active = audio_active;
    }

    // Check screen stream
    if (this.screen_stream && !this.disconnected_reported.screen) {
      const screen_tracks = this.screen_stream.getVideoTracks();
      const screen_active = screen_tracks.some((t) => t.readyState === "live");

      if (this.device_state.screen.active && !screen_active) {
        console.log(
          "[Hardware] Screen share became inactive during health check",
        );
        this.handleTrackEnded("screen");
      }

      this.device_state.screen.active = screen_active;
    }
  }

  /**
   * Attempt to recover a disconnected device (auto-recovery mode)
   * @param {string} device_type - 'camera', 'mic', or 'screen'
   * @returns {Promise<MediaStream|null>} New stream or null if failed
   */
  async attemptRecovery(device_type) {
    // Determine recovery type (camera/mic share the same recovery)
    const recovery_type = device_type === "screen" ? "screen" : "camera";

    // Prevent concurrent recovery attempts for same type
    if (this.is_recovering[recovery_type]) {
      console.log(
        `[Hardware] Recovery already in progress for ${recovery_type}`,
      );
      return null;
    }

    this.is_recovering[recovery_type] = true;

    console.log(`[Hardware] Attempting recovery for ${device_type}`);
    this.callbacks.on_recovery_started?.(device_type);

    try {
      let stream;

      if (recovery_type === "camera") {
        stream = await this.recoverCamera();
      } else {
        stream = await this.recoverScreen();
      }

      if (stream) {
        console.log(`[Hardware] Successfully recovered ${device_type}`);
        this.is_recovering[recovery_type] = false;
        this.callbacks.on_device_reconnected?.(device_type, stream);
        return stream;
      }
    } catch (error) {
      console.error(
        `[Hardware] Recovery attempt failed for ${device_type}:`,
        error,
      );

      // Wait before retrying
      await this.sleep(this.config.recovery_delay_ms);

      this.is_recovering[recovery_type] = false;

      // Retry recursively (user must share, so keep trying)
      return this.attemptRecovery(device_type);
    }

    this.is_recovering[recovery_type] = false;
    return null;
  }

  /**
   * Recover camera and microphone stream
   * @returns {Promise<MediaStream>} New camera stream
   */
  async recoverCamera() {
    // Stop existing tracks
    if (this.camera_stream) {
      this.camera_stream.getTracks().forEach((t) => t.stop());
    }

    // Request new stream - 720p for live view and recording
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        width: { ideal: 1280 },
        height: { ideal: 720 },
        frameRate: { ideal: 15, max: 15 },
        codec: "h264",
      },
      audio: true,
    });

    // Update internal reference
    this.camera_stream = stream;

    // Monitor new tracks
    stream.getVideoTracks().forEach((t) => this.monitorTrack(t, "camera"));
    stream.getAudioTracks().forEach((t) => this.monitorTrack(t, "mic"));

    // Update state - reset disconnected flags
    this.device_state.camera.active = true;
    this.device_state.mic.active = true;
    this.disconnected_reported.camera = false;
    this.disconnected_reported.mic = false;

    return stream;
  }

  /**
   * Recover screen share stream
   * @returns {Promise<MediaStream>} New screen stream
   */
  async recoverScreen() {
    // Stop existing tracks
    if (this.screen_stream) {
      this.screen_stream.getTracks().forEach((t) => t.stop());
    }

    // Request new screen share - 720p @ 10fps for text clarity
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: {
        displaySurface: "monitor",
        width: { ideal: 1280 },
        height: { ideal: 720 },
        frameRate: { ideal: 10, max: 10 },
        codec: "h264",
      },
      audio: true,
      preferCurrentTab: false,
      selfBrowserSurface: "exclude",
      surfaceSwitching: "exclude",
      monitorTypeSurfaces: "include",
    });

    // Validate entire screen selection
    const video_track = stream.getVideoTracks()[0];
    const settings = video_track.getSettings();

    if (settings.displaySurface !== "monitor") {
      stream.getTracks().forEach((t) => t.stop());
      throw new Error(
        `User selected ${settings.displaySurface} instead of entire screen`,
      );
    }

    // Update internal reference
    this.screen_stream = stream;

    // Monitor new tracks
    stream.getVideoTracks().forEach((t) => this.monitorTrack(t, "screen"));

    // Update state - reset disconnected flag
    this.device_state.screen.active = true;
    this.disconnected_reported.screen = false;

    return stream;
  }

  /**
   * Get current device status
   * @returns {Object} Device status for camera, mic, and screen
   */
  getDeviceStatus() {
    return {
      camera: {
        active: this.device_state.camera.active,
        muted: this.device_state.camera.muted,
      },
      mic: {
        active: this.device_state.mic.active,
        muted: this.device_state.mic.muted,
      },
      screen: {
        active: this.device_state.screen.active,
        muted: this.device_state.screen.muted,
      },
      is_monitoring: this.is_monitoring,
    };
  }

  /**
   * Get current streams
   * @returns {Object} Current camera and screen streams
   */
  getStreams() {
    return {
      camera: this.camera_stream,
      screen: this.screen_stream,
    };
  }

  /**
   * Utility sleep function
   * @param {number} ms - Milliseconds to sleep
   */
  sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Stop monitoring and cleanup
   */
  stop() {
    // Clear health check interval
    if (this.health_check_interval) {
      clearInterval(this.health_check_interval);
      this.health_check_interval = null;
    }

    // Remove device change listener
    if (navigator.mediaDevices) {
      navigator.mediaDevices.ondevicechange = null;
    }

    // Hide and cleanup dialog
    this.hideRecoveryDialog();
    const container = document.getElementById("hardware-recovery-container");
    if (container) {
      container.remove();
    }
    this.shadow_root = null;

    // Reset state
    this.camera_stream = null;
    this.screen_stream = null;
    this.is_monitoring = false;

    this.device_state = {
      camera: { active: false, muted: false },
      mic: { active: false, muted: false },
      screen: { active: false, muted: false },
    };

    this.is_recovering = {
      camera: false,
      screen: false,
    };

    this.disconnected_reported = {
      camera: false,
      mic: false,
      screen: false,
    };

    console.log("[Hardware] Stopped monitoring");
  }
}

export default new Hardware();
